import waFetch from 'globals/wa-fetch';
import { useState, useEffect, useRef } from 'react';
import { Formik, Form, FormikHelpers } from 'formik';
import { getUrl, goTo } from 'router/navigation';
import { connectWithProvider } from 'app/hermes-redux';
import { EMAIL_REGEX } from 'globals/regex';
import { showToast } from 'app/react-components/toaster-comp/state/actions';
import { ToastType } from 'react-components/toaster-comp/state/types';
import HelpCenterComp from 'app/react-components/help-center-comp/help-center-comp';
import TextInputField from 'wa-storybook/components/inputs/simple-input-field/text-input-comp';
import CheckboxComp from 'wa-storybook/components/inputs/checkbox-comp/checkbox-comp';
import Spinner from 'wa-storybook/components/general/spinner/spinner';
import Button from 'wa-storybook/components/general/button/button';
import GetHelpCard from './get-help-card/get-help-card';
import styles from './login-form.module.scss';
import classNames from 'classnames';
import type { Model as SessionModel } from 'state/session/types';
import type { Dispatch } from 'globals/types';
import ComponentAnimation, {
  AnimationTypes,
} from 'wa-storybook/components/general/component-animation/component-animation';
import { getKeys, set as storageSet, setPersistence } from 'globals/storage';
import { getLoginErrorMessage, setToken } from 'state/session/helpers';
import { setCompany } from 'stores/company-store';
import { login } from 'app/state/session/actions';
import getFormSchemas from './form-schemas';
import PageTitle from 'head/state/actions';
import { LoginFormData } from 'state/session/types';
import { beginMFASetup } from '../../../run';
import { EnvController } from 'react-components/env-controller/env-controller';
import { lookupKeys, performTenancyLookup } from 'globals/regional-api';
import {
  mfaChallengeNames,
  numbersOnlyRegex,
  verificationCodeLength,
} from '../../mfa-setup/state/constants';
import { sanitizeHtml } from 'wa-storybook/helpers/sanitize';
import {
  errorCodes,
  parseErrorCode,
} from 'wa-storybook/global/constants/error-messages';
import * as Storage from '../../../globals/storage';
import CookieUtils from 'globals/cookie-preferences';
import { isUserSSO } from './helpers';
import { DisasterRecovery } from '../disaster-recovery/disaster-recovery';
import { Link } from 'router/custom-link';
import {
  DisasterRecoveryService,
  DisasterRecoveryContext,
} from 'globals/context/disaster-recovery-context';
import { getZendeskLink } from 'state/global/actions';

type Props = {
  openHelpModal: () => void;
  hasPhoneNumbers: boolean;
  session: SessionModel;
  dispatch: Dispatch;
};

export const LoginForm = (props: Props) => {
  const isInDisasterRecoveryMode = React.useContext(DisasterRecoveryContext);

  useEffect(() => {
    props.dispatch(
      PageTitle.setCurrentSection(
        polyglot.t('components.main_menu.login_telus'),
      ),
    );
  }, []);

  const {
    usernameOnly,
    usernamePasswordAndRememberMe,
    usernamePasswordAndMFACode,
  } = getFormSchemas();

  const [validationSchema, setValidationSchema] = useState(usernameOnly);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [
    isPasswordFieldAtLeastPartiallyOpen,
    setIsPasswordFieldAtLeastPartiallyOpen,
  ] = useState<boolean>(false);
  const [isPasswordFieldOpen, setIsPasswordFieldOpen] =
    useState<boolean>(false);
  const [requiresMFAToLogIn, setRequiresMFAToLogIn] = useState<boolean>(false);

  const submitLoginDetails = async (
    data: LoginFormData,
    formikBag: FormikHelpers<LoginFormData>,
  ) => {
    const dataWithTrimmedUsername = {
      ...data,
      username: data.username.trim(), // remove any pasted spaces after email address
    } as const;

    try {
      const isEmail = EMAIL_REGEX.test(dataWithTrimmedUsername.username);

      await performTenancyLookup(
        isEmail ? lookupKeys.email : lookupKeys.keyword,
        dataWithTrimmedUsername.username,
      );

      if (!isPasswordFieldAtLeastPartiallyOpen) {
        const sso = await isUserSSO(dataWithTrimmedUsername.username);

        if (sso.status === 400 || sso.status === 404) {
          // continue with password opening logic in the following catch
          throw sso;
        } else if (sso.status !== 200) {
          props.dispatch(
            showToast({
              type: ToastType.ERROR,
              message: polyglot.t('global.update_error'),
            }),
          );
          if (sso.status === 500) {
            DisasterRecoveryService.setIsInDisasterRecoveryMode(true);
          }
          return;
        }

        const ssoJSON = await sso.json();

        setCompany({ sso: ssoJSON });

        const returnUrl = encodeURI(window.location.href);
        goTo('ssoRedirect', { returnUrl });
      } else {
        const formData: {
          email: string;
          password: string;
          remember?: boolean;
          mfa_code?: string;
        } = {
          email: dataWithTrimmedUsername.username,
          password: dataWithTrimmedUsername.password,
        };

        if (requiresMFAToLogIn) {
          formData.mfa_code = dataWithTrimmedUsername.mfa_code;
        } else {
          formData.remember = dataWithTrimmedUsername.remember_me;
        }

        const { body: authMobileResponse } = await waFetch('/auth/mobile', {
          method: 'post',
          version: 'json',
          body: formData,
        });

        props.dispatch({ type: 'SESSION/LOGIN_USER' });

        setPersistence(dataWithTrimmedUsername.remember_me || false);

        setToken(authMobileResponse.token);

        storageSet(
          getKeys().tokenExpiryTime,
          authMobileResponse.user['token_expiry_time'],
        );

        await props.dispatch(login(authMobileResponse));
      }
    } catch (res: any) {
      if (res.error !== 'redirecting') {
        props.dispatch({ type: 'SESSION/LOGIN_USER_FAILURE' });

        if (
          res.error?.code !==
            parseErrorCode(errorCodes['mfa_code_required'][0]) &&
          res.error?.code !== parseErrorCode(errorCodes['mfa_code_invalid'][0])
        ) {
          // Got a 400 or 404 from entry_points so open the password field
          setValidationSchema(usernamePasswordAndRememberMe);
        }
        if (res.error?.code === 500) {
          DisasterRecoveryService.setIsInDisasterRecoveryMode(true);
        }

        if (!isPasswordFieldAtLeastPartiallyOpen) {
          // same value as $passwordOpeningTransition in login-form.module.scss
          const passwordOpeningTransitionDurationInMS = 300;

          setIsPasswordFieldAtLeastPartiallyOpen(true);

          setTimeout(() => {
            setIsPasswordFieldOpen(true);
          }, passwordOpeningTransitionDurationInMS);
        } else {
          if (res.body?.mfa) {
            if (
              res.error?.code ===
              parseErrorCode(errorCodes['mfa_code_invalid'][0])
            ) {
              setLoginError(polyglot.t('login.mfa.code_required'));
              return;
            } else if (
              res.error?.code ===
              parseErrorCode(errorCodes['mfa_code_required'][0])
            ) {
              handleMFAChallenge(res.body, formikBag.validateForm);
              return;
            }
          }

          if (
            requiresMFAToLogIn &&
            res.error?.code === parseErrorCode(errorCodes['locked_account'][0])
          ) {
            setLoginError(polyglot.t('login.mfa.too_many_codes'));
          } else {
            setLoginError(getLoginErrorMessage(res));
          }
        }
      }
    }
  };

  const handleMFAChallenge = (
    {
      token,
      mfa: { challenge_name: challengeName },
      user: { user_id: userId },
    },
    validateForm,
  ) => {
    if (challengeName === mfaChallengeNames.MFA_SETUP) {
      Storage.setPersistence(true);
      setToken(token);
      beginMFASetup(userId);
    } else if (challengeName === mfaChallengeNames.SOFTWARE_TOKEN_MFA) {
      setValidationSchema(usernamePasswordAndMFACode);
      validateForm();
      setRequiresMFAToLogIn(true);
    }
  };

  const passwordRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (passwordRef?.current && isPasswordFieldOpen) {
      passwordRef.current.focus();
    }
  }, [passwordRef, isPasswordFieldOpen]);

  const mfaRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (mfaRef.current && requiresMFAToLogIn) {
      mfaRef.current.focus();
    }
  }, [mfaRef, requiresMFAToLogIn]);

  const initialValues: LoginFormData = {
    username: '',
    password: '',
    remember_me: false,
    mfa_code: '',
  };

  const showEnvironmentSelector = !IS_TEST && !IS_PRODUCTION;

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={submitLoginDetails}
      validationSchema={validationSchema}
    >
      {({
        errors = {},
        values = {},
        resetForm,
        isSubmitting,
        dirty,
        isValid,
        setFieldValue,
        setFieldError,
        validateForm,
      }) => (
        <Form className={styles.container}>
          {requiresMFAToLogIn ? (
            <div className={styles['content--mfa']}>
              <h1 className={classNames(styles.title, styles['title--mfa'])}>
                {polyglot.t('login.mfa.title')}
              </h1>
              <h2 className={styles.subtitle}>
                {polyglot.t('login.mfa.subtitle')}
              </h2>
              <div
                className={classNames(
                  styles['mfa-field'],
                  loginError && styles['mfa-field--error'],
                )}
              >
                <TextInputField
                  ref={mfaRef}
                  id='mfa_code'
                  label={polyglot.t('login.mfa.input_placeholder_expanded')}
                  reducedLabel={polyglot.t('login.mfa.input_placeholder')}
                  onChange={value => {
                    if (loginError) {
                      setLoginError(null);
                    }

                    const isNumber = new RegExp(numbersOnlyRegex);

                    if (
                      (isNumber.test(value) || value.length === 0) &&
                      value.length <= verificationCodeLength
                    ) {
                      setFieldValue('mfa_code', value);
                    }
                  }}
                  type='text'
                  parentErrorMessage={loginError}
                  exactValue={values.mfa_code}
                />
              </div>
              <div
                className={styles.contact}
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(
                    polyglot.t('login.mfa.contact_link', {
                      url: getZendeskLink({ key: 'help_centre' }),
                    }),
                  ),
                }}
              />
              <Button
                dataHook='mfa-login-continue-button'
                text={polyglot.t('login.mfa.button')}
                isDisabled={!dirty || !isValid}
                isLoading={isSubmitting}
                type='submit'
              />
            </div>
          ) : (
            <div className={styles['content--login']}>
              <h1 className={classNames(styles.title, styles['title--login'])}>
                {polyglot.t('login.section_title')}
              </h1>
              {props.session.fetching && (
                <div className={styles.overlay}>
                  <Spinner top={'calc(50% - 30px)'} />
                </div>
              )}
              <div className={styles['username-field']}>
                <TextInputField
                  autocomplete={'username'}
                  id='username'
                  ellipsifyExpandedLabel
                  label={polyglot.t('login.username')}
                  exactValue={values.username}
                  onChange={async value => {
                    if (isPasswordFieldAtLeastPartiallyOpen) {
                      setValidationSchema(usernameOnly);

                      resetForm({
                        values: {
                          username: '',
                          password: '',
                          remember_me: false,
                        },
                      });

                      setFieldError('password', undefined);

                      setIsPasswordFieldAtLeastPartiallyOpen(false);
                      setIsPasswordFieldOpen(false);
                      setLoginError(null);

                      await validateForm();
                    }

                    setFieldValue('username', value);
                  }}
                  parentErrorMessage={errors['username']}
                  type='text'
                  isRequired={true}
                />
              </div>

              <div
                className={classNames(
                  styles['password-field'],
                  isPasswordFieldAtLeastPartiallyOpen &&
                    styles['password-field--at-least-partially-open'],
                  isPasswordFieldOpen && styles['password-field--open'],
                  isPasswordFieldOpen &&
                    !!errors['password'] &&
                    styles['password-field--open-with-error'],
                )}
                aria-hidden={!isPasswordFieldAtLeastPartiallyOpen}
              >
                <div className={styles['password-field__inner']}>
                  <TextInputField
                    ref={passwordRef}
                    autocomplete={'current-password'}
                    id='password'
                    isReadOnly={false}
                    label={polyglot.t('login.password')}
                    tabIndex={isPasswordFieldAtLeastPartiallyOpen ? 0 : -1}
                    exactValue={values.password}
                    onChange={value => {
                      setFieldValue('password', value);
                      setLoginError(null);
                    }}
                    parentErrorMessage={errors['password']}
                    type='password'
                    isRequired={true}
                  />
                </div>
              </div>

              {loginError && (
                <ComponentAnimation
                  type={AnimationTypes.EXPAND_DOWN_AND_FADE_IN}
                  id='login-error-mesage'
                >
                  <div role='alert' className={styles.error}>
                    {loginError}
                  </div>
                </ComponentAnimation>
              )}
              {isInDisasterRecoveryMode && <DisasterRecovery />}
              <div
                className={classNames(
                  styles['cta-wrapper'],
                  !!errors['username'] &&
                    styles['cta-wrapper--with-username-error'],
                )}
              >
                <div className={styles['cta-wrapper__checkbox-wrapper']}>
                  {isPasswordFieldAtLeastPartiallyOpen && (
                    <CheckboxComp
                      id='remember'
                      label={polyglot.t('login.remember_checkbox')}
                      isChecked={values['remember_me']}
                      handleChange={e => {
                        setFieldValue('remember_me', e.target.checked, false);
                      }}
                    />
                  )}
                </div>

                <div className={styles.button}>
                  <Button
                    dataHook='login'
                    text={polyglot.t(
                      `login.${
                        !isPasswordFieldAtLeastPartiallyOpen ? 'next' : 'action'
                      }`,
                    )}
                    isDisabled={!dirty || !isValid}
                    isLoading={isSubmitting}
                    type='submit'
                  />
                </div>
              </div>

              {isPasswordFieldAtLeastPartiallyOpen && (
                <Link
                  to={getUrl('forgottenPassword')}
                  className={styles['forgot-password']}
                  onKeyDown={e => e.stopPropagation()}
                >
                  {polyglot.t('login.forgotten_your_password')}
                </Link>
              )}
            </div>
          )}

          {showEnvironmentSelector && (
            <div className={styles['env-controller']}>
              <EnvController />
            </div>
          )}

          <div className={styles.info}>
            {!!props.hasPhoneNumbers && (
              <GetHelpCard onClick={props.openHelpModal} />
            )}

            <HelpCenterComp
              helpText={polyglot.t(
                'sign_up.invitation.help_and_accessibility_text_link',
                {
                  urlGettingStarted: getZendeskLink({ key: 'getting_started' }),
                  urlAccessibilityTips: getZendeskLink({
                    key: 'accessibility_tips',
                  }),
                },
              )}
            />

            <button
              onClick={() => CookieUtils.launchCookieModal(1)}
              className={styles['cookie-link']}
            >
              {polyglot.t('login.cookie_preference_update')}
            </button>

            <div className={styles.terms}>
              {polyglot.t('login.add_info_3')}
              <a
                target='_blank'
                rel='noopener noreferrer'
                href={getZendeskLink({
                  key: 'user_terms',
                })}
                className={styles.link}
                onKeyDown={e => e.stopPropagation()}
              >
                {polyglot.t('login.add_info_4')}
              </a>
              {polyglot.t('login.add_info_5')}
              <a
                target='_blank'
                rel='noopener noreferrer'
                href={getZendeskLink({ key: 'privacy_policy' })}
                className={styles.link}
                onKeyDown={e => e.stopPropagation()}
              >
                {polyglot.t('login.add_info_6')}
              </a>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
};

LoginForm.displayName = 'LoginForm';

export default connectWithProvider(({ session }) => ({ session }), LoginForm);
